import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { X, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/primitives";

function tierLabel(u) {
  if (u.account_type === "business") return "Business";
  if (["pro", "promo", "comp"].includes(u.plan)) return "Plus";
  return "Free";
}

const PulseDot = ({ size = "h-2.5 w-2.5" }) => (
  <span className={`relative flex ${size}`}>
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
    <span className={`relative inline-flex rounded-full ${size} bg-green-500`} />
  </span>
);

export default function OnlineNowBubble({ className = "" }) {
  const { features } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const enabled = !features || features.online_now !== false;
  const { data } = useQuery({
    queryKey: ["online-now"],
    queryFn: () => api("/online"),
    refetchInterval: 30000,
    staleTime: 15000,
    enabled,
  });
  if (!enabled) return null;
  const count = data?.count ?? 0;
  const users = data?.users ?? [];

  return (
    <>
      <button
        type="button"
        data-testid="online-now-bubble"
        onClick={() => setOpen(true)}
        title={`${count} ${count === 1 ? "member" : "members"} online now`}
        className={`inline-flex items-center gap-2 rounded-pill bg-ink/5 hover:bg-ink/10 border border-line px-3 py-1.5 transition-colors ${className}`}
      >
        <PulseDot />
        <span className="text-sm font-medium text-ink whitespace-nowrap" data-testid="online-now-count">{count} online</span>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="online-now-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] flex flex-col shadow-xl animate-rise">
            <div className="flex items-center justify-between p-4 border-b border-line">
              <div className="flex items-center gap-2">
                <PulseDot />
                <h2 className="font-serif text-xl text-ink" data-testid="online-now-title">Online now · {count}</h2>
              </div>
              <button data-testid="online-now-close" onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-surface-3 flex items-center justify-center text-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-2">
              {users.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground"><Users className="w-8 h-8 mx-auto mb-2 opacity-50" />No one else is online right now.</div>
              ) : users.map((u) => (
                <button
                  key={u.user_id}
                  data-testid={`online-now-user-${u.user_id}`}
                  onClick={() => { setOpen(false); nav(`/member/${u.user_id}`); }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-surface-2 transition-colors text-left"
                >
                  <Avatar uri={u.avatar_url} name={u.full_name} size={44} online />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-ink truncate">{u.full_name}</p>
                    {u.title && <p className="text-muted-foreground text-xs truncate">{u.title}</p>}
                  </div>
                  <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-brand-tint text-brand-tint-ink shrink-0">{tierLabel(u)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
